/** Hue 0–360, saturation and value 0–1: the picker's own state, so a grey keeps its hue. */
export interface Hsv {
  h: number
  s: number
  v: number
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

/**
 * A typed or pasted colour as #rrggbb, else null. With or without #, 3 or 6
 * digits; while typing (`final` false) only 6 count, so "abc" on its way to
 * "abcdef" is not applied as #aabbcc.
 */
export function parseHex(text: string, final = true): string | null {
  const bare = text.trim().replace(/^#/, '')
  if (/^[0-9a-f]{6}$/i.test(bare)) return `#${bare.toLowerCase()}`
  if (final && /^[0-9a-f]{3}$/i.test(bare)) return `#${[...bare.toLowerCase()].map((c) => c + c).join('')}`
  return null
}

export function hexToHsv(hex: string): Hsv {
  const n = parseInt(hex.slice(1), 16)
  const r = ((n >> 16) & 255) / 255
  const g = ((n >> 8) & 255) / 255
  const b = (n & 255) / 255
  const max = Math.max(r, g, b)
  const d = max - Math.min(r, g, b)
  let h = 0
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
  }
  return { h: (h * 60 + 360) % 360, s: max === 0 ? 0 : d / max, v: max }
}

export function hsvToHex({ h, s, v }: Hsv): string {
  const f = (k: number) => {
    const p = (k + h / 60) % 6
    const c = v - v * s * Math.max(0, Math.min(p, 4 - p, 1))
    return Math.round(c * 255).toString(16).padStart(2, '0')
  }
  return `#${f(5)}${f(3)}${f(1)}`
}

/** The colour at a point of the area: saturation left to right, brightness bottom to top. */
export function areaAt(hue: number, x: number, y: number, width: number, height: number): Hsv {
  return { h: hue, s: clamp(x / width, 0, 1), v: clamp(1 - y / height, 0, 1) }
}

/** The hue at a point of the slider, left to right. */
export const hueAt = (x: number, width: number) => clamp((x / width) * 360, 0, 360)

/** Arrow keys on the area: a step of 1%, 10% with Shift. Null for any other key. */
export function areaKey(hsv: Hsv, key: string, shift: boolean): Hsv | null {
  const d = shift ? 0.1 : 0.01
  switch (key) {
    case 'ArrowLeft': return { ...hsv, s: clamp(hsv.s - d, 0, 1) }
    case 'ArrowRight': return { ...hsv, s: clamp(hsv.s + d, 0, 1) }
    case 'ArrowDown': return { ...hsv, v: clamp(hsv.v - d, 0, 1) }
    case 'ArrowUp': return { ...hsv, v: clamp(hsv.v + d, 0, 1) }
  }
  return null
}

/** Arrow, Home and End keys on the hue slider: a degree, ten with Shift. */
export function hueKey(hue: number, key: string, shift: boolean): number | null {
  const d = shift ? 10 : 1
  switch (key) {
    case 'ArrowLeft':
    case 'ArrowDown': return clamp(hue - d, 0, 360)
    case 'ArrowRight':
    case 'ArrowUp': return clamp(hue + d, 0, 360)
    case 'Home': return 0
    case 'End': return 360
  }
  return null
}
